// Payments data layer (Phase 4, P4-CC1) — discrete payments against invoices.
//
// The SINGLE interface between the app and public.invoice_payments
// (supabase/migrations/20260706170000_invoice_payments.sql).
//
// SOURCE-OF-TRUTH MODEL: these rows are the ONLY record of how much an invoice is
// paid. amount_paid / amount_outstanding / the paid|partial status are DERIVED ON
// READ from them via the `invoice_ledger` view (invoices.js reads that view). We
// NEVER write the invoice row on payment activity — so WP9's issued-invoice
// immutability lock is never even challenged (payments touch only this table).
//
// SECURITY (server-enforced; mirrored here): company_id is inherited from the
// parent invoice by trigger (immutable), payment_number auto = PAY-00001, RLS
// scopes all. A soft `voided` flag excludes a payment from the paid sum while
// keeping it visible for audit.

import { supabase } from "./supabaseClient";
import { todayISO } from "./dates";

const T = "invoice_payments";

/** Methods (value + label) — parity with the DB CHECK. */
export const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "card", label: "Card" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

const SELECT = "id, invoice_id, payment_number, amount, payment_date, method, reference, notes, voided, voided_at, created_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid payment — check the amount or method."; break;
    case "23503": message = "That invoice no longer exists."; break;
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You do not have access to this invoice."; break;
    case "PGRST116": message = "Payment not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/**
 * Record a payment against an ISSUED invoice by INSERTING a payment row. The
 * invoice's amount_paid/outstanding/status are re-derived on read (the ledger
 * view) — this never writes the invoice. Back-compatible with WP9's
 * recordPayment(id, amount): accepts a bare number OR a full payload object.
 * @param {string} invoiceId
 * @param {number|{amount:number, payment_date?:string, method?:string, reference?:string, notes?:string}} amountOrPayload
 * @returns {Promise<string>} the invoice id.
 */
export async function recordPayment(invoiceId, amountOrPayload) {
  const c = client();
  const p = (amountOrPayload && typeof amountOrPayload === "object") ? amountOrPayload : { amount: amountOrPayload };
  const amount = round2(p.amount);
  if (!(amount > 0)) { const e = new Error("Enter a payment amount greater than zero."); e.code = "BAD_AMOUNT"; throw e; }
  // Payments may only be recorded against an issued invoice (not a draft/cancelled one).
  const { data: inv, error: gErr } = await c.from("invoices").select("status").eq("id", invoiceId).maybeSingle();
  if (gErr) throw toError(gErr, "Could not load the invoice");
  if (!inv) throw toError({ code: "PGRST116" }, "Invoice not found");
  if (inv.status === "draft") { const e = new Error("Send the invoice before recording a payment."); e.code = "NOT_ISSUED"; throw e; }
  if (inv.status === "cancelled") { const e = new Error("This invoice is cancelled."); e.code = "CANCELLED"; throw e; }
  const row = {
    invoice_id: invoiceId,
    amount,
    payment_date: p.payment_date || todayISO(),
    method: p.method || "bank_transfer",
    reference: (p.reference && String(p.reference).trim()) || null,
    notes: (p.notes && String(p.notes).trim()) || null,
  };
  const { error } = await c.from(T).insert(row).select("id").single();
  if (error) throw toError(error, "Could not record the payment");
  return invoiceId;
}

/** All payments for an invoice, newest first — INCLUDING voided ones (audit trail). */
export async function listPayments(invoiceId) {
  const { data, error } = await client().from(T).select(SELECT)
    .eq("invoice_id", invoiceId)
    .order("payment_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw toError(error, "Could not load payments");
  return data || [];
}

/** Soft-void a payment: excluded from the paid sum, kept visible for audit. */
export async function voidPayment(paymentId) {
  const { error } = await client().from(T)
    .update({ voided: true, voided_at: new Date().toISOString() })
    .eq("id", paymentId).select("id").single();
  if (error) throw toVoidError(error);
  return paymentId;
}

/**
 * voidPayment's own mapper. It exists for ONE code and defers everything else.
 *
 * ⚠️ WHY 42501 IS PASSED THROUGH HERE AND NOWHERE ELSE — THE REASONING, NOT THE
 * EDIT, IS WHAT MATTERS. DO NOT "SIMPLIFY" THIS BACK INTO toError().
 *
 * On THIS statement — an UPDATE of invoice_payments — a 42501 can only have come
 * from a TRIGGER, never from RLS:
 *
 *   • A row the caller cannot see fails the policy's USING clause and is simply
 *     not matched. That returns ZERO ROWS, which .single() surfaces as PGRST116
 *     — not 42501. RLS hides; it does not shout.
 *   • WITH CHECK can raise 42501, but only when the NEW row would fall outside
 *     the policy. Voiding changes `voided` and `voided_at` and nothing else — not
 *     company_id, not invoice_id — so if USING passed, WITH CHECK passes too.
 *
 * So the only source of 42501 here is trg_50_void_lock
 * (20260819120000_cc26c1b_post_receipt.sql), whose message is written for a
 * human and says what to actually do: reverse the ledger entry first. The
 * shared toError() maps every 42501 to "You do not have access to this invoice",
 * which on this path is not merely generic — it is WRONG, and it sends the
 * operator to check permissions when the instruction is something else entirely.
 * A wrong explanation costs more than a generic one.
 *
 * ⛔ NOT changed globally, deliberately. On recordPayment's INSERT a 42501
 * genuinely IS RLS WITH CHECK — inserting against another company's invoice —
 * and there the access sentence is correct. That path still uses toError().
 */
function toVoidError(error) {
  if (error && error.code === "42501" && error.message) {
    const e = new Error(error.message); e.code = error.code; e.cause = error;
    return e;
  }
  return toError(error, "Could not void the payment");
}

/**
 * Post a recorded receipt to the general ledger (P4-CC26c-1b).
 *
 * Thin by design: every rule lives in post_payment_to_ledger() in
 * 20260819120000_cc26c1b_post_receipt.sql, where the verification estate can
 * reach it. This maps its refusals to sentences a business owner can act on.
 *
 * POSTED-STATE IS DERIVED, never stored: a payment is posted iff a journal
 * exists with source_type='invoice_payment' and its id. There is deliberately no
 * `posted` column to drift out of step with the ledger.
 *
 * @param {string} paymentId
 * @returns {Promise<string>} the journal id
 */
export async function postToLedger(paymentId) {
  const { data, error } = await client().rpc("post_payment_to_ledger", { p_payment_id: paymentId });
  if (error) throw toLedgerError(error);
  return data;
}

// Ledger refusals carry codes the generic toError() does not know about, and
// each means something specific the user can act on. Same shape as
// src/lib/invoices.js's toLedgerError(): switch on the code, never on the
// message, and keep the original as `cause`.
function toLedgerError(error) {
  const code = error && error.code;
  const raw = (error && error.message) || "";
  let message;
  switch (code) {
    case "23505":
      message = "This payment has already been posted to the ledger.";
      break;
    case "42501":
      // A voided payment, or no write permission on accounts. The database's
      // own sentence already names the payment, so it is passed through.
      message = raw || "This payment cannot be posted.";
      break;
    case "P0002":
      message = "Payment not found, or you do not have access to it.";
      break;
    case "P0001":
      // Three shapes: the invoice is not posted yet, a system account is
      // missing from the chart, or the VAT release would be negative. Each
      // names exactly what is wrong and what to do; a friendlier rewrite would
      // throw that away.
      message = raw || "This payment could not be posted to the ledger.";
      break;
    case "23514":
      // ⚠️ 23514 IS EVERY CHECK CONSTRAINT ON journals AND journal_lines, NOT
      // ONLY post_journal()'s balance assertion. This branch used to name
      // balance outright — and when a source_type violation came through it
      // reported a balance failure, pointing whoever read it at the one thing
      // that was working. The harness only survived it because it also printed
      // the raw cause; a UI showing the mapped message alone would have sent
      // someone hunting the balance logic for hours.
      //
      // The "this is a defect" framing is still true and stays. What changes is
      // that the database's own words are CARRIED THROUGH — they contain the
      // constraint name (journals_source_type_chk, journal_lines_one_side_chk,
      // journals_source_basis_chk, …), which is the one detail that identifies
      // which rule was broken. An error mapper that discards the identifying
      // detail is the same family as a discarded HTTP status.
      message = `The ledger entry for this payment was rejected by the database. This is a defect, not a data-entry problem — please report it, with this detail: ${raw || "(the database supplied no detail)"}`;
      break;
    default:
      message = raw || "Could not post the payment to the ledger.";
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}
